import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Select,
  Space,
  Typography,
  Alert,
  Collapse,
  Tag,
  Divider,
  Spin,
  message,
  InputNumber,
  Card,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ApiOutlined,
  SendOutlined,
  CheckOutlined,
  KeyOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { autoReplyAPI } from '../services/api';

const { Text, Paragraph } = Typography;

// 可用的 Telegram 上下文参数列表
const AVAILABLE_PARAMS: Array<{ param: string; label: string; example: string; group: string }> = [
  { param: 'telegram_user_id',    label: '用户ID',    example: '123456789',   group: '用户' },
  { param: 'telegram_username',   label: '@username', example: 'johndoe',     group: '用户' },
  { param: 'telegram_first_name', label: '名字',      example: '张三',         group: '用户' },
  { param: 'telegram_last_name',  label: '姓氏',      example: '李',           group: '用户' },
  { param: 'bot_id',          label: '机器人ID',       example: '987654321',          group: '机器人' },
  { param: 'bot_username',    label: '机器人@username', example: 'mybot',              group: '机器人' },
  { param: 'bot_name',        label: '机器人名称',      example: 'My Bot',             group: '机器人' },
  { param: 'bot_invite_link', label: '机器人邀请链接',  example: 'https://t.me/mybot', group: '机器人' },
  { param: 'telegram_group_id',          label: '群组ID',       example: '-100123456',              group: '群组' },
  { param: 'telegram_group_title',       label: '群组标题',     example: '我的群组',                 group: '群组' },
  { param: 'telegram_group_invite_link', label: '群组邀请链接', example: 'https://t.me/joinchat/x', group: '群组' },
  { param: 'start_param', label: '深度链接参数', example: 'agent_007', group: '用户' },
];

const PARAM_EXAMPLE: Record<string, string> = Object.fromEntries(
  AVAILABLE_PARAMS.map(p => [p.param, p.example])
);
const PARAM_LABEL: Record<string, string> = Object.fromEntries(
  AVAILABLE_PARAMS.map(p => [p.param, p.label])
);

export interface RequestBodyParam {
  id: string;
  key: string;
  param: string;
}

export interface CustomHeader {
  id: string;
  name: string;
  value_from_env: string;
}

export interface QueryParam {
  id: string;
  key: string;
  param: string;
}

export interface AuthCodeConfig {
  variable_name: string;
  expires_minutes: number;
}

/** 单个 API 请求配置 */
export interface ApiRequestConfig {
  id: string;
  name: string;
  api_url: string;
  method: 'GET' | 'POST';
  custom_headers?: CustomHeader[];
  request_body_params?: RequestBodyParam[];
  query_params?: QueryParam[];
  response_fields: string[];
}

/**
 * 动态变量配置格式（新格式，支持多请求）
 * 兼容旧格式：如果没有 requests 字段，则视为单请求配置
 */
export interface DynamicVariableConfig {
  // 新格式
  requests?: ApiRequestConfig[];
  auth_codes?: AuthCodeConfig[];
  // 旧格式兼容字段（读取时转换）
  api_url?: string;
  method?: 'GET' | 'POST';
  custom_headers?: CustomHeader[];
  request_body_params?: RequestBodyParam[];
  query_params?: QueryParam[];
  response_fields?: string[];
}

interface Props {
  value?: DynamicVariableConfig | null;
  onChange?: (v: DynamicVariableConfig | null) => void;
}

let _nextId = 1;
const newId = () => `id${_nextId++}`;

const newRequestConfig = (): ApiRequestConfig => ({
  id: newId(),
  name: '',
  api_url: '',
  method: 'POST',
  custom_headers: [{ id: newId(), name: 'X-API-Key', value_from_env: 'TGBOT_API_KEY' }],
  request_body_params: [{ id: newId(), key: 'username', param: 'telegram_username' }],
  query_params: [],
  response_fields: [],
});

const emptyConfig = (): DynamicVariableConfig => ({
  requests: [newRequestConfig()],
  auth_codes: [],
});

/** 将旧格式（单请求）转换为新格式（requests 数组） */
function normalizeConfig(raw: DynamicVariableConfig): DynamicVariableConfig {
  if (raw.requests) {
    // 已经是新格式，确保 id 存在
    return {
      ...raw,
      requests: raw.requests.map(r => ({
        ...r,
        id: r.id || newId(),
        custom_headers: (r.custom_headers || []).map(h => ({ ...h, id: (h as any).id || newId() })),
        request_body_params: (r.request_body_params || []).map(p => ({ ...p, id: p.id || newId() })),
        query_params: (r.query_params || []).map(p => ({ ...p, id: (p as any).id || newId() })),
      })),
    };
  }
  // 旧格式：转换为新格式
  if (raw.api_url) {
    return {
      requests: [{
        id: newId(),
        name: '',
        api_url: raw.api_url,
        method: raw.method || 'POST',
        custom_headers: (raw.custom_headers || []).map(h => ({ ...h, id: (h as any).id || newId() })),
        request_body_params: (raw.request_body_params || []).map(p => ({ ...p, id: p.id || newId() })),
        query_params: (raw.query_params || []).map(p => ({ ...p, id: (p as any).id || newId() })),
        response_fields: raw.response_fields || [],
      }],
      auth_codes: raw.auth_codes || [],
    };
  }
  // 没有 requests 也没有 api_url，但可能有 auth_codes
  return {
    ...emptyConfig(),
    auth_codes: raw.auth_codes || [],
  };
}

/** 单个 API 请求配置编辑面板 */
const SingleRequestEditor: React.FC<{
  config: ApiRequestConfig;
  onChange: (next: ApiRequestConfig) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  requestIndex: number;
  totalRequests: number;
}> = ({ config, onChange, onDelete, onDuplicate, requestIndex, totalRequests }) => {
  const [testContext, setTestContext] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    status?: number;
    data?: any;
    error?: string;
  } | null>(null);

  const update = (patch: Partial<ApiRequestConfig>) => {
    onChange({ ...config, ...patch });
  };

  // custom_headers
  const customHeaders = config.custom_headers || [];
  const addCustomHeader = () => {
    update({ custom_headers: [...customHeaders, { id: newId(), name: '', value_from_env: '' }] });
  };
  const updateCustomHeader = (idx: number, patch: Partial<CustomHeader>) => {
    update({ custom_headers: customHeaders.map((h, i) => i === idx ? { ...h, ...patch } : h) });
  };
  const removeCustomHeader = (idx: number) => {
    update({ custom_headers: customHeaders.filter((_, i) => i !== idx) });
  };

  // request_body_params
  const bodyParams = config.request_body_params || [];
  const usedBodyParams = new Set(bodyParams.map(p => p.param));
  const addBodyParam = (paramName?: string) => {
    const param = paramName || 'telegram_user_id';
    if (bodyParams.some(p => p.param === param)) return;
    update({ request_body_params: [...bodyParams, { id: newId(), key: param, param }] });
  };
  const updateBodyParamKey = (idx: number, key: string) => {
    update({ request_body_params: bodyParams.map((p, i) => i === idx ? { ...p, key } : p) });
  };
  const removeBodyParam = (idx: number) => {
    update({ request_body_params: bodyParams.filter((_, i) => i !== idx) });
  };

  // query_params
  const queryParams = config.query_params || [];
  const addQueryParam = (paramName?: string) => {
    const param = paramName || 'telegram_user_id';
    update({ query_params: [...queryParams, { id: newId(), key: param, param }] });
  };
  const updateQueryParamKey = (idx: number, key: string) => {
    update({ query_params: queryParams.map((p, i) => i === idx ? { ...p, key } : p) });
  };
  const removeQueryParam = (idx: number) => {
    update({ query_params: queryParams.filter((_, i) => i !== idx) });
  };

  // response_fields
  const addResponseField = () => {
    update({ response_fields: [...config.response_fields, ''] });
  };
  const updateResponseField = (idx: number, val: string) => {
    update({ response_fields: config.response_fields.map((f, i) => i === idx ? val : f) });
  };
  const removeResponseField = (idx: number) => {
    update({ response_fields: config.response_fields.filter((_, i) => i !== idx) });
  };
  const addResponseFieldFromResult = (fieldName: string) => {
    if (config.response_fields.includes(fieldName)) return;
    update({ response_fields: [...config.response_fields, fieldName] });
  };

  const getTestValue = (paramName: string): string =>
    testContext[paramName] ?? PARAM_EXAMPLE[paramName] ?? '';
  const setTestValue = (paramName: string, val: string) => {
    setTestContext(prev => ({ ...prev, [paramName]: val }));
  };

  // URL 模板预览
  const previewUrl = useMemo(() => {
    return config.api_url.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, paramName) => {
      return getTestValue(paramName) || match;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.api_url, testContext]);
  const urlHasTemplate = /\{[a-zA-Z_]/.test(config.api_url);

  // 请求体预览
  const previewRequestBody = useMemo(() => {
    if (bodyParams.length === 0) return { telegram_user_id: getTestValue('telegram_user_id') };
    return Object.fromEntries(bodyParams.filter(p => p.key).map(p => [p.key, getTestValue(p.param)]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyParams, testContext]);

  // Query 参数预览
  const previewQueryParams = useMemo(() => {
    if (queryParams.length === 0) return {};
    return Object.fromEntries(queryParams.filter(p => p.key).map(p => [p.key, getTestValue(p.param)]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, testContext]);

  // 测试输入框的参数列表
  const testInputParams = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ param: string; usage: string }> = [];
    // URL 模板
    const urlMatches = config.api_url.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g);
    if (urlMatches) {
      for (const m of urlMatches) {
        const p = m.slice(1, -1);
        if (!seen.has(p)) { seen.add(p); result.push({ param: p, usage: 'URL 路径' }); }
      }
    }
    if (config.method === 'POST') {
      for (const bp of bodyParams) {
        if (bp.param && !seen.has(bp.param)) { seen.add(bp.param); result.push({ param: bp.param, usage: `请求体 (${bp.key})` }); }
      }
    }
    if (config.method === 'GET') {
      for (const qp of queryParams) {
        if (qp.param && !seen.has(qp.param)) { seen.add(qp.param); result.push({ param: qp.param, usage: `Query (${qp.key})` }); }
      }
    }
    return result;
  }, [bodyParams, config.method, config.api_url, queryParams]);

  // 测试
  const handleTest = async () => {
    if (!config.api_url) { message.warning('请先填写接口地址'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await autoReplyAPI.testDynamicVar({
        api_url: config.api_url,
        method: config.method,
        custom_headers: customHeaders.filter(h => h.name && h.value_from_env).map(h => ({ name: h.name, value_from_env: h.value_from_env })),
        request_body_params: bodyParams,
        query_params: queryParams,
        test_context: {
          telegram_user_id: Number(getTestValue('telegram_user_id')) || undefined,
          telegram_username: getTestValue('telegram_username') || undefined,
          telegram_first_name: getTestValue('telegram_first_name') || undefined,
          telegram_last_name: getTestValue('telegram_last_name') || undefined,
          bot_id: Number(getTestValue('bot_id')) || undefined,
          bot_username: getTestValue('bot_username') || undefined,
          bot_name: getTestValue('bot_name') || undefined,
          bot_invite_link: getTestValue('bot_invite_link') || undefined,
          telegram_group_id: Number(getTestValue('telegram_group_id')) || undefined,
          telegram_group_title: getTestValue('telegram_group_title') || undefined,
          telegram_group_invite_link: getTestValue('telegram_group_invite_link') || undefined,
        },
      });
      setTestResult(res.data);
    } catch (err: any) {
      setTestResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setTesting(false);
    }
  };

  const collectFieldPaths = (obj: any, prefix = ''): string[] => {
    if (!obj || typeof obj !== 'object') return [];
    if (Array.isArray(obj)) return prefix ? [prefix] : [];
    return Object.keys(obj).flatMap(k => {
      const path = prefix ? `${prefix}.${k}` : k;
      const val = obj[k];
      if (val && typeof val === 'object' && !Array.isArray(val) && prefix === '') {
        return [path, ...Object.keys(val).map(kk => `${path}.${kk}`)];
      }
      return [path];
    });
  };

  const configuredFields = config.response_fields.filter(f => f.trim());

  return (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      title={
        <Space>
          <Text strong style={{ fontSize: 13 }}>
            请求 {requestIndex + 1}
            {config.name && <span style={{ fontWeight: 'normal', color: '#666' }}> - {config.name}</span>}
          </Text>
          {configuredFields.length > 0 && (
            <Space size={4}>
              {configuredFields.map(f => {
                const parts = f.split(':');
                const name = parts[1] || parts[0].split('.').pop() || '';
                return <Tag key={f} color="blue" style={{ fontSize: 11 }}>{`{${name}}`}</Tag>;
              })}
            </Space>
          )}
        </Space>
      }
      extra={
        <Space size={4}>
          {onDuplicate && (
            <Button size="small" icon={<CopyOutlined />} onClick={onDuplicate} title="复制此请求" />
          )}
          {onDelete && totalRequests > 1 && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete} title="删除此请求" />
          )}
        </Space>
      }
    >
      {/* 请求名称 */}
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>请求名称（可选，方便区分）</Text>
        <Input
          size="small"
          placeholder="如：查询用户推广信息"
          value={config.name}
          onChange={e => update({ name: e.target.value })}
          style={{ marginTop: 2 }}
        />
      </div>

      {/* 接口地址 */}
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          接口地址
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, color: '#999' }}>
            支持 {'{param_name}'} 占位符
          </Text>
        </Text>
        <Input
          size="small"
          placeholder="http://127.0.0.1:8080/api/v1/tgbot/users/lookup"
          value={config.api_url}
          onChange={e => update({ api_url: e.target.value })}
          style={{ marginTop: 2 }}
        />
        {urlHasTemplate && (
          <Text type="secondary" style={{ fontSize: 11, color: '#1890ff' }}>预览：{previewUrl}</Text>
        )}
      </div>

      {/* 请求方式 */}
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>请求方式</Text>
        <Select
          size="small"
          value={config.method}
          onChange={val => update({ method: val })}
          style={{ width: '100%', marginTop: 2 }}
          options={[
            { value: 'POST', label: 'POST' },
            { value: 'GET', label: 'GET' },
          ]}
        />
      </div>

      <Divider style={{ margin: '6px 0' }} />

      {/* 自定义请求头 */}
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          自定义请求头
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, color: '#999' }}>密钥值从服务器 .env 读取</Text>
        </Text>
        {customHeaders.map((item, idx) => (
          <Space key={item.id} style={{ marginBottom: 4, display: 'flex', marginTop: idx === 0 ? 4 : 0 }}>
            <Input size="small" placeholder="X-API-Key" style={{ width: 130 }} value={item.name} onChange={e => updateCustomHeader(idx, { name: e.target.value })} />
            <Text type="secondary" style={{ fontSize: 11 }}>= env:</Text>
            <Input size="small" placeholder="TGBOT_API_KEY" style={{ width: 180 }} value={item.value_from_env} onChange={e => updateCustomHeader(idx, { value_from_env: e.target.value })} />
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeCustomHeader(idx)} />
          </Space>
        ))}
        <Button size="small" icon={<PlusOutlined />} onClick={addCustomHeader} style={{ marginTop: 4 }}>添加请求头</Button>
      </div>

      <Divider style={{ margin: '6px 0' }} />

      {/* POST 请求体参数 */}
      {config.method === 'POST' && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>请求体参数 (JSON Body)</Text>
          <div style={{ marginTop: 4, marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {AVAILABLE_PARAMS.map(({ param, label }) => (
              <Tag
                key={param}
                color={usedBodyParams.has(param) ? 'blue' : 'default'}
                style={{ cursor: usedBodyParams.has(param) ? 'default' : 'pointer', userSelect: 'none', fontSize: 11 }}
                onClick={() => !usedBodyParams.has(param) && addBodyParam(param)}
              >
                {usedBodyParams.has(param) && <CheckOutlined style={{ marginRight: 2 }} />}
                {param}
              </Tag>
            ))}
          </div>
          {bodyParams.map((item, idx) => (
            <Space key={item.id} style={{ marginBottom: 4, display: 'flex' }}>
              <Input size="small" placeholder="发送字段名" style={{ width: 140 }} value={item.key} onChange={e => updateBodyParamKey(idx, e.target.value)} />
              <Text type="secondary" style={{ fontSize: 11 }}>&larr;</Text>
              <Select
                size="small" style={{ width: 180 }} value={item.param}
                onChange={val => update({ request_body_params: bodyParams.map((p, i) => i === idx ? { id: p.id, key: val, param: val } : p) })}
                options={AVAILABLE_PARAMS.map(p => ({ value: p.param, label: `${p.param} (${p.label})` }))}
              />
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBodyParam(idx)} />
            </Space>
          ))}
          <Button size="small" icon={<PlusOutlined />} onClick={() => addBodyParam(AVAILABLE_PARAMS.find(p => !usedBodyParams.has(p.param))?.param)} style={{ marginTop: 4 }}>添加参数</Button>
        </div>
      )}

      {/* GET Query 参数 */}
      {config.method === 'GET' && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Query 参数 (?key=value)
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, color: '#999' }}>不配置则不携带任何查询参数</Text>
          </Text>
          {queryParams.map((item, idx) => (
            <Space key={item.id} style={{ marginBottom: 4, display: 'flex', marginTop: idx === 0 ? 4 : 0 }}>
              <Input size="small" placeholder="Query key" style={{ width: 140 }} value={item.key} onChange={e => updateQueryParamKey(idx, e.target.value)} />
              <Text type="secondary" style={{ fontSize: 11 }}>&larr;</Text>
              <Select
                size="small" style={{ width: 180 }} value={item.param}
                onChange={val => update({ query_params: queryParams.map((p, i) => i === idx ? { ...p, key: p.key || val, param: val } : p) })}
                options={AVAILABLE_PARAMS.map(p => ({ value: p.param, label: `${p.param} (${p.label})` }))}
              />
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeQueryParam(idx)} />
            </Space>
          ))}
          <Button size="small" icon={<PlusOutlined />} onClick={() => addQueryParam('telegram_user_id')} style={{ marginTop: 4 }}>添加 Query 参数</Button>
        </div>
      )}

      <Divider style={{ margin: '6px 0' }} />

      {/* 提取响应字段 */}
      <div style={{ marginBottom: 8 }}>
        <Space style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>提取响应字段</Text>
          <Button size="small" icon={<PlusOutlined />} onClick={addResponseField}>添加</Button>
        </Space>
        <div style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>
            格式：字段路径:变量名，如 data.user_id:platform_uid；不填别名则取路径最后一段
          </Text>
        </div>
        {config.response_fields.map((field, idx) => {
          const parts = field.split(':');
          const fieldPath = parts[0] || '';
          const alias = parts[1] || '';
          const displayName = alias || fieldPath.split('.').pop() || '';
          return (
            <Space key={`rf-${idx}`} style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
              <Input
                size="small" placeholder="data.user_id" style={{ width: 160 }}
                value={fieldPath}
                onChange={e => {
                  const newVal = alias ? `${e.target.value}:${alias}` : e.target.value;
                  updateResponseField(idx, newVal);
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>:</Text>
              <Input
                size="small" placeholder="自定义变量名（可选）" style={{ width: 140 }}
                value={alias}
                onChange={e => {
                  const newVal = e.target.value ? `${fieldPath}:${e.target.value}` : fieldPath;
                  updateResponseField(idx, newVal);
                }}
              />
              <Tag color="blue" style={{ fontSize: 11 }}>{`{${displayName}}`}</Tag>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeResponseField(idx)} />
            </Space>
          );
        })}
      </div>

      <Divider style={{ margin: '6px 0' }} />

      {/* 测试 */}
      <div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>测试发送</Text>
        {testInputParams.map(({ param, usage }) => (
          <Space key={param} style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            <Input
              size="small" style={{ width: 180 }}
              value={testContext[param] ?? PARAM_EXAMPLE[param] ?? ''}
              onChange={e => setTestValue(param, e.target.value)}
              placeholder={PARAM_EXAMPLE[param] || param}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {PARAM_LABEL[param] || param}
              <span style={{ color: '#bbb', marginLeft: 4 }}>&rarr; {usage}</span>
            </Text>
          </Space>
        ))}

        {config.method === 'POST' && bodyParams.length > 0 && (
          <pre style={{ margin: '4px 0 8px', fontSize: 11, background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 4, padding: '4px 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(previewRequestBody, null, 2)}
          </pre>
        )}
        {config.method === 'GET' && queryParams.length > 0 && (
          <pre style={{ margin: '4px 0 8px', fontSize: 11, background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 4, padding: '4px 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            Query: {JSON.stringify(previewQueryParams, null, 2)}
          </pre>
        )}

        <Button size="small" icon={<SendOutlined />} loading={testing} onClick={handleTest} type="primary" ghost>
          发送测试
        </Button>

        {testing && <Spin size="small" style={{ marginTop: 6, display: 'block' }} />}

        {testResult && (
          <div style={{ marginTop: 6 }}>
            {testResult.success ? (
              <>
                <Text type="success" style={{ fontSize: 12 }}>
                  HTTP {testResult.status} -- 点击字段名可快速添加
                </Text>
                <div style={{ background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 4, padding: '6px 8px', marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                </div>
                {testResult.data && typeof testResult.data === 'object' && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {collectFieldPaths(testResult.data).map(path => {
                      const varName = path.split('.').pop() as string;
                      const isAdded = config.response_fields.includes(path) || config.response_fields.includes(varName);
                      return (
                        <Tag
                          key={path} color={isAdded ? 'green' : 'processing'}
                          style={{ cursor: isAdded ? 'default' : 'pointer', fontSize: 11 }}
                          onClick={() => !isAdded && addResponseFieldFromResult(path)}
                        >
                          {isAdded && <CheckOutlined style={{ marginRight: 2 }} />}
                          {path}
                        </Tag>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Alert type="error" showIcon style={{ marginTop: 4 }} message={testResult.error || '请求失败'} />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// ============== 主组件 ==============
const DynamicVariableEditor: React.FC<Props> = ({ value, onChange }) => {
  const config = value ? normalizeConfig(value) : null;
  const requests = config?.requests || [];
  const authCodes = config?.auth_codes || [];

  const update = (next: DynamicVariableConfig | null) => {
    onChange?.(next);
  };

  const handleEnable = () => update(emptyConfig());
  const handleDisable = () => update(null);

  // 输出给父组件时，只保留 requests + auth_codes（不含旧格式字段）
  const updateRequests = (nextRequests: ApiRequestConfig[]) => {
    update({ requests: nextRequests, auth_codes: authCodes });
  };

  const updateRequest = (idx: number, next: ApiRequestConfig) => {
    updateRequests(requests.map((r, i) => i === idx ? next : r));
  };

  const addRequest = () => {
    updateRequests([...requests, newRequestConfig()]);
  };

  const deleteRequest = (idx: number) => {
    updateRequests(requests.filter((_, i) => i !== idx));
  };

  const duplicateRequest = (idx: number) => {
    const src = requests[idx];
    const copy: ApiRequestConfig = {
      ...JSON.parse(JSON.stringify(src)),
      id: newId(),
      name: src.name ? `${src.name} (副本)` : '',
    };
    const next = [...requests];
    next.splice(idx + 1, 0, copy);
    updateRequests(next);
  };

  // auth_codes
  const updateAuthCodes = (next: AuthCodeConfig[]) => {
    update({ requests, auth_codes: next });
  };
  const addAuthCode = () => updateAuthCodes([...authCodes, { variable_name: '', expires_minutes: 10 }]);
  const updateAuthCode = (idx: number, patch: Partial<AuthCodeConfig>) => {
    updateAuthCodes(authCodes.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };
  const removeAuthCode = (idx: number) => updateAuthCodes(authCodes.filter((_, i) => i !== idx));

  // 所有请求配置的提取字段汇总
  const allConfiguredFields = requests.flatMap(r => r.response_fields.filter(f => f.trim()));

  if (!value) {
    return (
      <div style={{ padding: '8px 0' }}>
        <Button icon={<ApiOutlined />} onClick={handleEnable} size="small">
          启用动态变量（调用外部 API）
        </Button>
        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          可在发送前自动从你的服务器获取登录链接、Token 等动态内容
        </Text>
      </div>
    );
  }

  return (
    <Collapse
      defaultActiveKey={['config']}
      style={{ marginBottom: 0 }}
      items={[
        {
          key: 'config',
          label: (
            <Space>
              <ApiOutlined />
              <Text strong>动态变量 API 配置</Text>
              <Tag color="default" style={{ fontSize: 11 }}>{requests.length} 个请求</Tag>
              {allConfiguredFields.length > 0 && (
                <Space size={4}>
                  {allConfiguredFields.slice(0, 6).map(f => {
                    const parts = f.split(':');
                    const name = parts[1] || parts[0].split('.').pop() || '';
                    return <Tag key={f} color="blue" style={{ fontSize: 11 }}>{`{${name}}`}</Tag>;
                  })}
                  {allConfiguredFields.length > 6 && <Text type="secondary" style={{ fontSize: 11 }}>+{allConfiguredFields.length - 6}</Text>}
                </Space>
              )}
            </Space>
          ),
          children: (
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="工作原理"
                description={
                  <Paragraph style={{ margin: 0, fontSize: 12 }}>
                    机器人发送消息时，会<b>并行调用</b>下方配置的所有接口，并将返回字段合并注入为模板变量。
                    <br />
                    例如请求1返回 <Text code>{'data.user_id'}</Text>，请求2返回 <Text code>{'data.downlines'}</Text>，
                    则可在回复中同时使用 <Text code>{'{user_id}'}</Text> 和 <Text code>{'{downlines}'}</Text>。
                    <br />
                    URL 支持 <Text code>{'{telegram_user_id}'}</Text> 等占位符。接口鉴权通过「自定义请求头」配置，密钥从服务器环境变量读取。
                  </Paragraph>
                }
              />

              {requests.map((req, idx) => (
                <SingleRequestEditor
                  key={req.id}
                  config={req}
                  onChange={next => updateRequest(idx, next)}
                  onDelete={() => deleteRequest(idx)}
                  onDuplicate={() => duplicateRequest(idx)}
                  requestIndex={idx}
                  totalRequests={requests.length}
                />
              ))}

              <Button icon={<PlusOutlined />} onClick={addRequest} style={{ marginBottom: 12 }} block>
                添加 API 请求
              </Button>

              <Button size="small" danger onClick={handleDisable}>
                禁用动态变量
              </Button>
            </div>
          ),
        },
        {
          key: 'auth_codes',
          label: (
            <Space>
              <KeyOutlined />
              <Text strong>登录短码变量</Text>
              {authCodes.filter(c => c.variable_name).length > 0 && (
                <Space size={4}>
                  {authCodes.filter(c => c.variable_name).map(c => (
                    <Tag key={c.variable_name} color="orange">{`{${c.variable_name}}`}</Tag>
                  ))}
                </Space>
              )}
            </Space>
          ),
          children: (
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="工作原理"
                description={
                  <Paragraph style={{ margin: 0, fontSize: 12 }}>
                    机器人发送消息时，自动生成一次性短码（16字符），嵌入消息文本或按钮 URL 中。
                    <br />
                    你的 Web 服务收到短码后，调用 <Text code>POST /api/tg-auth/exchange</Text>（携带 <Text code>X-API-Key</Text>）换取用户信息，实现免密登录。
                  </Paragraph>
                }
              />
              {authCodes.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                  暂未配置短码变量，点击下方按钮添加
                </Text>
              )}
              {authCodes.map((item, idx) => (
                <Space key={idx} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                  <Input
                    size="small" placeholder="auth_code" style={{ width: 160 }} value={item.variable_name}
                    onChange={e => updateAuthCode(idx, { variable_name: e.target.value })}
                    addonBefore="{" addonAfter="}"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>有效期</Text>
                  <InputNumber
                    size="small" min={1} max={1440} value={item.expires_minutes}
                    onChange={v => updateAuthCode(idx, { expires_minutes: v || 10 })}
                    style={{ width: 80 }} addonAfter="分钟"
                  />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeAuthCode(idx)} />
                </Space>
              ))}
              <Button size="small" icon={<PlusOutlined />} onClick={addAuthCode} style={{ marginTop: 4 }}>添加短码变量</Button>
              {authCodes.filter(c => c.variable_name).length > 0 && (
                <Alert
                  type="success" showIcon style={{ marginTop: 12 }}
                  message={
                    <span style={{ fontSize: 12 }}>
                      在回复中使用：
                      {authCodes.filter(c => c.variable_name).map(c => (
                        <Text key={c.variable_name} code style={{ marginLeft: 4 }}>{`{${c.variable_name}}`}</Text>
                      ))}
                    </span>
                  }
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default DynamicVariableEditor;
